import { Dispatch, useCallback, useMemo } from 'react'

import {
  CashRequestMiddleware,
  CashResponseMiddleware,
  ensureHeadersRequest,
  OptionalMiddlewareContextProps,
  rejectOnErrorResponse,
} from '@cash-web/shared-util-fetch'

import { createFlowRequestMiddleware } from '../middleware/request'
import {
  createSetClientScenarioMiddleware,
  createSetFlowTokenMiddleware,
  createSetPaymentTokensMiddleware,
  createSetTransferTokenMiddleware,
} from '../middleware/response'
import { useCreateCdfJourneyOutcomeResponseMiddleware } from '../middleware/response/useCreateCdfJourneyOutcomeResponseMiddleware'
import { excludeDeprecatedRequestMiddleware, excludeResponseMiddleware } from '../middleware/utils'
import { FlowReducerAction, FlowState } from './flowReducer'

// used to automate re-assignment blocker related properties
export const useFlowOptionalMiddleware = ({
  state,
  dispatch,
}: {
  state: FlowState
  dispatch: Dispatch<FlowReducerAction>
}) => {
  const cdfJourneyOutcomeMiddleware = useCreateCdfJourneyOutcomeResponseMiddleware({ state })

  const requestMiddleware = useCallback(
    (middleware: CashRequestMiddleware[]) => {
      return [
        ensureHeadersRequest,
        createFlowRequestMiddleware({ state, dispatch }),
        ...middleware.filter(excludeDeprecatedRequestMiddleware),
      ]
    },
    [dispatch, state]
  )

  const responseMiddleware = useCallback(
    (middleware: CashResponseMiddleware[]) => {
      return [
        ...middleware.filter(excludeResponseMiddleware),
        createSetFlowTokenMiddleware({ state, dispatch }),
        createSetClientScenarioMiddleware({ state, dispatch }),
        createSetTransferTokenMiddleware({ state, dispatch }),
        createSetPaymentTokensMiddleware({ state, dispatch }),
        cdfJourneyOutcomeMiddleware,
        // we want to handle rejections after we have processed the response
        rejectOnErrorResponse,
      ]
    },
    [dispatch, state, cdfJourneyOutcomeMiddleware]
  )

  // the optional middleware is responsible for automatically updating the flow token / client scenario
  // and the response context from the last request.
  const optionalMiddleware: OptionalMiddlewareContextProps = useMemo(() => {
    // we only want to attach our middleware if we are in progress
    // this is especially important with the global provider
    // as this will break the legacy flow provider by removing it's middleware
    return {
      useFlowOptionalMiddleware: {
        request: requestMiddleware,
        response: responseMiddleware,
      },
    }
  }, [requestMiddleware, responseMiddleware])

  return optionalMiddleware
}
